// src/modules/operaciones/inventarios/components/ModalCausaNegativo.js
import React, { useState, useEffect, useRef, useCallback } from "react";
import { fmt, fmtFecha } from "../../../../utils/format";
import { useModalA11y } from "../../../../hooks/useModalA11y";
import { API_URL, pedirJson, colorSaldo } from "../utils/negativosComun";

// Clases completas y estáticas (Tailwind no genera clases construidas por interpolación).
const ESTILO_SEVERIDAD = {
  alta: "bg-red-500/15 text-red-600 border-red-500/40",
  media: "bg-amber-500/15 text-amber-700 border-amber-500/40",
  baja: "bg-blue-500/15 text-blue-600 border-blue-500/40",
};
const ESTILO_CONFIANZA = {
  alta: "bg-green-500/15 text-green-700 border-green-500/40",
  media: "bg-amber-500/15 text-amber-700 border-amber-500/40",
  baja: "bg-red-500/15 text-red-600 border-red-500/40",
};

const esAbort = (e) => e?.name === "AbortError";
const etiquetaClave = (k) => k.replace(/([A-Z])/g, " $1").toLowerCase();
const textoEvidencia = (v) =>
  Array.isArray(v) ? v.join(", ") : v !== null && typeof v === "object" ? JSON.stringify(v) : String(v);

function Dato({ titulo, tema, children }) {
  return (
    <div>
      <p className={tema.textSub}>{titulo}</p>
      {children}
    </div>
  );
}

function Advertencias({ advertencias, tema }) {
  if (!advertencias || advertencias.length === 0) return null;
  return (
    <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl text-xs">
      <p className={`font-extrabold mb-2 ${tema.textMain}`}>ℹ️ Advertencias de consistencia</p>
      <ul className="list-disc pl-5 space-y-1.5">
        {advertencias.map((a, idx) => (
          <li key={`${a.codigo}-${idx}`} className={`leading-relaxed ${tema.textMain}`}>{a.mensaje}</li>
        ))}
      </ul>
    </div>
  );
}

export default function ModalCausa({ articulo, onClose, tema }) {
  const { modalRef, inicialRef } = useModalA11y(onClose);
  const [paso, setPaso] = useState("bodegas");
  const [bodegas, setBodegas] = useState({ cargando: true, data: [], error: null });
  const [bodegaSeleccionada, setBodegaSeleccionada] = useState(null);
  const [causaData, setCausaData] = useState({ cargando: false, data: null, error: null });
  const causaCtrlRef = useRef(null);

  useEffect(() => {
    const ctrl = new AbortController();
    (async () => {
      try {
        const res = await pedirJson(`${API_URL}/negativos/${encodeURIComponent(articulo.ARTI_COD)}/bodegas`, ctrl.signal);
        if (!ctrl.signal.aborted) setBodegas({ cargando: false, data: res.bodegas || [], error: null });
      } catch (e) {
        if (!ctrl.signal.aborted && !esAbort(e)) setBodegas({ cargando: false, data: [], error: e.message });
      }
    })();
    return () => ctrl.abort();
  }, [articulo.ARTI_COD]);

  // Al desmontar el modal se cancela cualquier diagnóstico en vuelo.
  useEffect(() => () => causaCtrlRef.current?.abort(), []);

  const verCausaBodega = useCallback(async (b) => {
    causaCtrlRef.current?.abort();
    const ctrl = new AbortController();
    causaCtrlRef.current = ctrl;

    setBodegaSeleccionada(b);
    setPaso("causa");
    setCausaData({ cargando: true, data: null, error: null });
    try {
      const data = await pedirJson(
        `${API_URL}/negativos/${encodeURIComponent(articulo.ARTI_COD)}/causa?bodega=${encodeURIComponent(b.BODE_COD)}`,
        ctrl.signal
      );
      if (!ctrl.signal.aborted) setCausaData({ cargando: false, data, error: null });
    } catch (e) {
      if (!ctrl.signal.aborted && !esAbort(e)) setCausaData({ cargando: false, data: null, error: e.message });
    }
  }, [articulo.ARTI_COD]);

  const volverABodegas = () => {
    causaCtrlRef.current?.abort();
    setPaso("bodegas");
    setBodegaSeleccionada(null);
    setCausaData({ cargando: false, data: null, error: null });
  };

  // ==========================================
  // VISTA 1: LISTA DE BODEGAS NEGATIVAS
  // ==========================================
  if (paso === "bodegas") {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
        <div ref={modalRef} className={`${tema.bgCard} border ${tema.borderMain} rounded-3xl p-6 max-w-3xl w-full shadow-2xl flex flex-col`} onClick={(e) => e.stopPropagation()}>
          <h2 className={`text-lg font-extrabold ${tema.textMain} mb-4`}>Bodegas negativas de {articulo.ARTI_COD}</h2>
          <div className="flex-1 overflow-y-auto min-h-0">
            {bodegas.cargando ? (
              <p className={`text-sm py-4 ${tema.textSub}`}>Cargando bodegas...</p>
            ) : bodegas.error ? (
              <p className="text-sm py-4 text-red-500 font-bold">Error: {bodegas.error}</p>
            ) : bodegas.data.length === 0 ? (
              <p className={`text-sm py-4 ${tema.textSub}`}>No hay bodegas negativas hoy.</p>
            ) : bodegas.data.map((b) => (
              <button key={b.BODE_COD} onClick={() => verCausaBodega(b)} className={`w-full text-left ${tema.bgTarjeta} border ${tema.borderMain} p-4 rounded-xl flex justify-between mb-2 hover:border-red-400 transition-colors`}>
                <div>
                  <p className={`font-bold ${tema.textMain}`}>{b.BODE_COD} - {b.BODE_NOM}</p>
                  <p className={`text-xs ${tema.textSub}`}>{b.SUCU_NOMBRE}</p>
                </div>
                <p className="text-red-500 font-mono font-bold">{fmt(b.SALDO)}</p>
              </button>
            ))}
          </div>
          <button ref={inicialRef} onClick={onClose} className="w-full mt-4 py-3 bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-xl transition-colors">
            Cerrar
          </button>
        </div>
      </div>
    );
  }

  // ==========================================
  // VISTA 2: DIAGNÓSTICO (CAUSA)
  // ==========================================
  const data = causaData.data;
  const origen = data?.origen;
  const factor = data?.factorClave;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div ref={modalRef} className={`${tema.bgCard} border ${tema.borderMain} rounded-3xl p-6 max-w-4xl w-full max-h-[90vh] shadow-2xl flex flex-col`} onClick={(e) => e.stopPropagation()}>

        <div className="flex justify-between items-start mb-4">
          <button onClick={volverABodegas} className={`text-sm font-bold ${tema.textSub} hover:opacity-70 transition-opacity flex items-center gap-1`}>
            ← Volver a bodegas
          </button>
          <p className={`text-xs font-mono px-2 py-1 rounded bg-black/5 ${tema.textSub}`}>
            Bodega: {bodegaSeleccionada?.BODE_COD}{data?.bodega?.nombre ? ` · ${data.bodega.nombre}` : ""}
          </p>
        </div>

        <div className="flex-1 overflow-y-auto min-h-0 pr-1">
          {causaData.cargando ? (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <span className="inline-block w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />
              <p className={`text-sm font-bold ${tema.textMain}`}>Analizando existencias y kardex...</p>
            </div>
          ) : causaData.error ? (
            <div className="flex flex-col items-center gap-3 py-8">
              <p className="text-sm text-red-500 font-bold text-center">Error: {causaData.error}</p>
              <button onClick={() => verCausaBodega(bodegaSeleccionada)} className="px-4 py-2 rounded-lg text-xs font-bold border border-purple-500/40 bg-purple-500/10 text-purple-500 hover:bg-purple-500/20">
                Reintentar
              </button>
            </div>
          ) : data && origen ? (
            <div className="flex flex-col gap-4">

              {/* Origen del quiebre */}
              <div className={`p-4 border ${tema.borderMain} rounded-xl bg-black/5`}>
                <div className="flex justify-between items-center mb-3 gap-2 flex-wrap">
                  <p className={`text-xs uppercase tracking-wider font-bold ${tema.textSub}`}>Información del quiebre</p>
                  {origen.confianza && (
                    <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded-full border ${ESTILO_CONFIANZA[origen.confianza] || ESTILO_CONFIANZA.baja}`}>
                      Confianza {origen.confianza}
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                  <Dato titulo="Primer día en negativo" tema={tema}>
                    <p className={`font-bold text-lg ${tema.textMain}`}>{fmtFecha(origen.fechaOrigen)}</p>
                  </Dato>
                  <Dato titulo="Saldo previo al quiebre" tema={tema}>
                    <p className={`font-bold text-lg ${origen.saldoPrevio ? colorSaldo(origen.saldoPrevio.valor) : tema.textSub}`}>
                      {origen.saldoPrevio ? fmt(origen.saldoPrevio.valor) : "—"}
                    </p>
                    {origen.saldoPrevio?.fecha && (
                      <p className={`text-[10px] ${tema.textSub}`}>(cierre del {fmtFecha(origen.saldoPrevio.fecha)})</p>
                    )}
                    {origen.sinHistoricoSano && <p className={`text-[10px] ${tema.textSub}`}>Sin saldo sano registrado</p>}
                  </Dato>
                  <Dato titulo="Saldo actual" tema={tema}>
                    <p className={`font-bold text-lg ${colorSaldo(origen.saldoActual)}`}>{fmt(origen.saldoActual)}</p>
                  </Dato>
                  <Dato titulo="Días en negativo" tema={tema}>
                    <p className={`font-bold text-lg ${tema.textMain}`}>{origen.diasNegativo} día(s)</p>
                  </Dato>
                </div>
              </div>

              <Advertencias advertencias={data.advertencias} tema={tema} />

              {/* Factor clave */}
              {factor ? (
                <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl text-xs">
                  <p className="font-extrabold text-red-600 mb-1 flex items-center gap-2">
                    <span className="text-base">🚨</span> Factor clave (movimiento que causó el quiebre)
                  </p>
                  <p className={`${tema.textMain} font-medium mt-1`}>
                    {factor.tipoNombre} · <strong>{factor.prefijo}-{factor.numero}</strong> · {fmtFecha(factor.fecha)}
                  </p>
                  <p className={tema.textSub}>
                    Cantidad: {fmt(factor.cantidad)} · Saldo {fmt(factor.saldoAntes)} → {fmt(factor.saldoDespues)} · Concepto: {factor.concepto || "—"}
                  </p>
                </div>
              ) : (
                <p className={`text-xs ${tema.textSub}`}>No se pudo identificar un movimiento único que cause el quiebre.</p>
              )}

              {/* Sospechosos */}
              {data.sospechosos && data.sospechosos.length > 0 && (
                <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl text-xs">
                  <p className={`font-extrabold mb-3 flex items-center gap-2 ${tema.textMain}`}>
                    <span className="text-base">🔍</span> Posibles causas detectadas
                  </p>
                  <div className="flex flex-col gap-3">
                    {data.sospechosos.map((s, idx) => (
                      <div key={`${s.codigo}-${idx}`} className={`p-3 rounded-lg border ${tema.borderMain} bg-black/5`}>
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border ${ESTILO_SEVERIDAD[s.severidad] || ESTILO_SEVERIDAD.baja}`}>
                            {s.severidad}
                          </span>
                          <span className={`font-bold ${tema.textMain}`}>{s.titulo}</span>
                          {typeof s.confianza === "number" && (
                            <span className={`text-[10px] ${tema.textSub}`}>· {Math.round(s.confianza * 100)}% de certeza</span>
                          )}
                        </div>
                        <p className={`leading-relaxed ${tema.textMain}`}>{s.mensaje}</p>
                        {s.evidencia && Object.keys(s.evidencia).length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            {Object.entries(s.evidencia).map(([k, v]) => (
                              <span key={k} className={`text-[10px] font-mono px-1.5 py-0.5 rounded bg-black/5 ${tema.textSub}`}>
                                {etiquetaClave(k)}: {textoEvidencia(v)}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Ajuste relacionado */}
              {data.ajusteRelacionado && (
                <div className={`p-3 border ${tema.borderMain} rounded-xl text-xs`}>
                  <p className={`font-bold ${tema.textMain}`}>
                    Ajuste de inventario posterior: {data.ajusteRelacionado.PREF_PRE}-{data.ajusteRelacionado.AJUS_NUMERO} · {fmtFecha(data.ajusteRelacionado.AJUS_FECHA)}
                  </p>
                  <p className={tema.textSub}>{data.ajusteRelacionado.AJUS_CONC || "—"}</p>
                </div>
              )}

              {/* Movimientos del día */}
              <div>
                <p className={`text-xs uppercase tracking-wider font-bold ${tema.textSub} mb-2`}>
                  Movimientos del día del quiebre
                </p>
                {!data.movimientos || data.movimientos.length === 0 ? (
                  <p className={`text-sm ${tema.textSub} py-4`}>No hay movimientos registrados ese día.</p>
                ) : (
                  <div className={`overflow-x-auto border ${tema.borderMain} rounded-xl`}>
                    <table className={`w-full text-left text-xs ${tema.textSub}`}>
                      <thead className={`${tema.bgHeader} border-b ${tema.borderMain}`}>
                        <tr>
                          <th className="px-3 py-2 font-bold">Tipo</th>
                          <th className="px-3 py-2 font-bold">Documento</th>
                          <th className="px-3 py-2 font-bold">Concepto</th>
                          <th className="px-3 py-2 font-bold text-right">Cantidad</th>
                          <th className="px-3 py-2 font-bold text-right">Saldo antes</th>
                          <th className="px-3 py-2 font-bold text-right">Saldo después</th>
                        </tr>
                      </thead>
                      <tbody className={`divide-y ${tema.borderMain}`}>
                        {data.movimientos.map((m, i) => (
                          <tr key={`${m.cons ?? i}-${i}`} className={m.esFactorClave ? "bg-red-500/10" : "hover:bg-black/5"}>
                            <td className="px-3 py-2">{m.tipoNombre}</td>
                            <td className="px-3 py-2 font-mono">{m.prefijo}-{m.numero}</td>
                            <td className="px-3 py-2 max-w-[200px] truncate" title={m.concepto}>{m.concepto || "—"}</td>
                            <td className={`px-3 py-2 text-right font-mono font-bold ${colorSaldo(m.cantidad)}`}>{fmt(m.cantidad)}</td>
                            <td className={`px-3 py-2 text-right font-mono ${colorSaldo(m.saldoAntes)}`}>{m.saldoAntes != null ? fmt(m.saldoAntes) : "—"}</td>
                            <td className={`px-3 py-2 text-right font-mono font-bold ${colorSaldo(m.saldoDespues)}`}>{m.saldoDespues != null ? fmt(m.saldoDespues) : "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

            </div>
          ) : (
            <div className="flex flex-col gap-4 py-4">
              <p className={`text-center ${tema.textSub}`}>{data?.mensaje || "No se encontraron datos de causa para esta bodega."}</p>
              <Advertencias advertencias={data?.advertencias} tema={tema} />
            </div>
          )}
        </div>

        <button ref={inicialRef} onClick={onClose} className="w-full mt-6 py-3 bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-xl transition-colors">
          Cerrar
        </button>
      </div>
    </div>
  );
}